import { Outlet, useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { WuwEvent } from '@packages/types';
import { eventBySlugQuery } from '@/api/queries';
import { LangToggle, resolveEventTheme } from '@/components';
import { EventOutletContext } from './eventContext';

const EventHeader = ({ event }: { event: WuwEvent }) => {
  const { t } = useTranslation();
  const year = (() => {
    const d = new Date(event.startsAt);
    return Number.isNaN(d.getTime()) ? null : d.getFullYear();
  })();
  return (
    <header className="ev-header">
      <Link
        to={`/events/${event.slug}`}
        className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3.5 gap-y-1 overflow-hidden no-underline"
        style={{ color: 'var(--ev-ink)' }}>
        <span
          className="ev-disp min-w-0 max-w-full break-words"
          style={{ fontSize: 'clamp(16px, 4.8vw, 26px)', letterSpacing: '.02em' }}>
          {t('event_header_wordmark')}
        </span>
        {year ? (
          <span
            className="ev-mono"
            style={{
              fontSize: 12,
              color: 'var(--ev-accent2-readable, var(--ev-accent2))',
              letterSpacing: '.1em',
            }}>
            {year}
          </span>
        ) : null}
      </Link>
      <LangToggle variant="event" />
    </header>
  );
};

const EventFooter = () => {
  const { t } = useTranslation();
  return (
    <footer
      className="ev-mono"
      style={{
        position: 'relative',
        zIndex: 3,
        borderTop: '1px solid var(--ev-line)',
        padding: '24px 48px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
      }}>
      <span style={{ fontSize: 11, color: 'var(--ev-muted)', letterSpacing: '.1em' }}>
        {t('a_ww_event')}
      </span>
    </footer>
  );
};

const EventShell = ({ event, children }: { event: WuwEvent; children: React.ReactNode }) => (
  <div className="ev-root" style={resolveEventTheme(event.theme)}>
    <EventHeader event={event} />
    <div className="grow">{children}</div>
    <EventFooter />
  </div>
);

export const EventLayout = () => {
  const { t } = useTranslation();
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const { data: event, isLoading } = useQuery(eventBySlugQuery(eventSlug ?? ''));

  if (isLoading) {
    return (
      <div className="ev-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <span className="ev-mono" style={{ color: 'var(--ev-muted)', letterSpacing: '.1em' }}>
          {t('loading')}
        </span>
      </div>
    );
  }

  if (!event) {
    return (
      <div
        className="ev-root"
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: 40,
        }}>
        <div>
          <div className="ev-disp" style={{ fontSize: 56 }}>
            404
          </div>
          <p className="ev-mono" style={{ color: 'var(--ev-muted)', marginTop: 12 }}>
            {t('event_not_found')}
          </p>
          <a
            href="https://wuest-und-wild.de"
            className="ev-btn ev-btn--ghost"
            style={{ marginTop: 24 }}>
            ← wuest-und-wild.de
          </a>
        </div>
      </div>
    );
  }

  return (
    <EventShell event={event}>
      <Outlet context={{ event } satisfies EventOutletContext} />
    </EventShell>
  );
};
